/**
 * Software: the restart banner, the updates waiting, the library install, antivirus, the installed
 * applications, and the patches.
 *
 * Two rules carry over from the XAML, and both are about a status line rather than about software:
 *
 *   * **EVERY STATUS LINE IS ALWAYS IN THE TREE**, empty when idle. This tab is where the OpenSilver
 *     client learned that twice - a line bound to `Visibility` is hidden exactly while it has
 *     nothing to say, which is the state it is in when the request goes out, and revealing it
 *     afterwards inside an already-hidden tab panel left it unarranged. A failed uninstall therefore
 *     read as "nothing happens at all": the request went out, the device answered, and the answer
 *     had nowhere to appear. `StatusLine` is built for that and needs no care here.
 *   * **THE TWO LINES ARE SEPARATE.** Refreshes and uninstalls share one; installs from the library
 *     have their own. They are two conversations with the machine and one overwriting the other's
 *     answer loses whichever arrived first. The restart ack is a third and lives in the header, for
 *     the same reason the button does - it can be pressed from any tab.
 *
 * Everything here is Windows-only: the tab is hidden entirely for a Synology, which has no registry
 * uninstall list, no WUA and no Security Center.
 */
import { insets, type Control } from 'webwin';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import {
  SoftwareInstallState,
  type InstalledPatchDto,
  type InstalledSoftwareDto,
  type PendingUpdateDto,
  type SoftwareInstallJobDto,
  type SoftwareInventoryDto,
  type SoftwarePackageDto
} from '../../contracts.gen.js';
import { bytes, join, localDate, localDateTime, plural } from '../../format.js';
import { CardList } from '../../ui/CardList.js';
import { chip } from '../../ui/Chip.js';
import { confirm } from '../../ui/dialogs.js';
import { BoundedScroller } from '../../ui/Scroller.js';
import { StatusLine } from '../../ui/StatusLine.js';
import { button, buttonRow, card, comboBox, fillWith, FONT, GAP, h2, hstack, inlineRow, muted, relayout, splitRow, stackOf, text, textBox, value, vstack } from '../../ui/layout.js';
import type { DeviceTab, DeviceTabContext } from './context.js';

/** The XAML's cap. Above it the filter is the way to find a row, and the overflow line says so. */
const MAX_SOFTWARE_ROWS = 250;

export interface SoftwareTab extends DeviceTab {
  /** Re-reads the inventory. The shell calls this on `SoftwareInventoryChanged`. */
  refresh(): Promise<void>;
  /** A progress push for this device's install job. */
  applyInstallJob(job: SoftwareInstallJobDto): void;
  /** An uninstall / patch / update-install outcome from the agent. */
  applyOutcome(line: string): void;
  /** True while a reboot or updates are pending - the strip's attention dot. */
  attention(): boolean;
  /** Presence moved: the per-row action buttons all gate on it. */
  applyPresence(): void;
}

export function softwareTab(dc: DeviceTabContext): SoftwareTab {
  let inventory: SoftwareInventoryDto | null = null;
  let packages: SoftwarePackageDto[] = [];
  let jobs: SoftwareInstallJobDto[] = [];
  let filter = '';
  let installing = false;

  const panel = vstack({ spacing: GAP.md, hAlign: 'stretch', autoWidth: false });
  const status = new StatusLine();
  const installStatus = new StatusLine();

  // ---- restart pending ----
  const rebootBanner = card({ tint: 'warn', bordered: false, padding: insets(GAP.md, GAP.sm) });
  const rebootRow = splitRow({ align: 'center' });
  fillWith(
    rebootRow,
    text('Restart pending — Windows is waiting for a reboot to finish installing changes.', {
      role: 'warn',
      font: FONT.small,
      wrap: true
    })
  );
  // Restart itself lives in the header, so the banner's button is the same action reached from a
  // second place rather than a second implementation of it.
  const restartFromBanner = button('Restart now', () => void dc.restart());
  rebootRow.controls.add(restartFromBanner);
  rebootBanner.controls.add(rebootRow);
  rebootBanner.visible = false;

  // ---- updates waiting ----
  const updateCount = value('', { font: FONT.small, role: 'muted' });
  const installAll = button('Install All', () => void installUpdates(), true);
  const updateHeader = stackOf(inlineRow(), value('Updates waiting', { font: FONT.h2 }), updateCount, installAll);

  const updateList = new CardList<PendingUpdateDto>({
    key: u => u.updateId || u.title,
    build: u => buildUpdateRow(u)
  });
  const updateScroller = new BoundedScroller(280);
  updateScroller.content.controls.add(updateList.panel);
  const updateCard = card();
  updateCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      updateScroller,
      text(
        "The list is what Windows Update's last scan found. Installs run as a batch in the background; " +
          'the device reports the outcome and a fresh inventory when it finishes.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );
  const updateSection = stackOf(
    vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false, visible: false }),
    updateHeader,
    updateCard
  );

  // ---- install from the library ----
  const packageEmpty = muted('');
  const packagePicker = comboBox<SoftwarePackageDto>([], {
    width: 380,
    autoWidth: false,
    itemText: p => p.displayName
  });
  const installButton = button('Install', () => void installPackage(), true);
  const installRow = stackOf(buttonRow(), packagePicker, installButton);

  const jobList = new CardList<SoftwareInstallJobDto>({
    key: j => j.id,
    version: j => [j.state, j.percent, j.message],
    build: j => buildJobRow(j),
    max: 20
  });
  const jobScroller = new BoundedScroller(240);
  jobScroller.content.controls.add(jobList.panel);
  const jobEmpty = muted('');

  const installCard = card();
  installCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      packageEmpty,
      installRow,
      text(
        'The agent downloads the installer, checks it against the hash the CRM holds, and runs it ' +
          'silently as SYSTEM. An offline device is not refused — the install is queued and runs when ' +
          'the agent next connects.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      ),
      installStatus.label,
      text('Recent installs', { font: FONT.bold, margin: insets(0, GAP.sm, 0, 0) }),
      jobEmpty,
      jobScroller
    )
  );

  // ---- security ----
  const avWaiting = muted("Waiting for the agent's first software report…");
  const avNone = card({ tint: 'danger', bordered: false, padding: insets(GAP.md, GAP.sm) });
  avNone.controls.add(
    text('No antivirus reported on this device.', { role: 'danger', font: FONT.small, wrap: true })
  );
  avNone.visible = false;
  const avList = vstack({ spacing: GAP.xs, hAlign: 'stretch', autoWidth: false });
  const avCard = card();
  avCard.controls.add(
    stackOf(vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }), avWaiting, avNone, avList)
  );

  // ---- installed software ----
  const softwareCount = value('', { font: FONT.small, role: 'muted' });
  const collected = value('', { font: FONT.small, role: 'muted' });
  const refreshButton = button('Refresh', () => void refreshInventory());
  // One wrapping line rather than a title beside a right-hand group: at phone width the group left
  // the title a word a line.
  const softwareHeader = stackOf(
    inlineRow(),
    value('Installed software', { font: FONT.h2 }),
    softwareCount,
    collected,
    refreshButton
  );

  const filterBox = textBox({ hAlign: 'stretch' });
  filterBox.on('textchanged', () => {
    filter = filterBox.text;
    applySoftwareRows();
    dc.relayout();
  });
  const overflow = muted('');
  const softwareList = new CardList<InstalledSoftwareDto>({
    key: s => s.key || s.name,
    build: s => buildSoftwareRow(s),
    max: MAX_SOFTWARE_ROWS
  });
  const softwareScroller = new BoundedScroller(340);
  softwareScroller.content.controls.add(softwareList.panel);
  const softwareCard = card();
  softwareCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      filterBox,
      text('Filter by name or publisher', { role: 'muted', font: FONT.tiny }),
      softwareScroller,
      overflow,
      text(
        'Only applications with a silent uninstaller show an Uninstall button — the rest must be ' +
          'removed at the machine.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );

  // ---- patches ----
  const patchCount = value('', { font: FONT.small, role: 'muted' });
  const patchWaiting = muted("Waiting for the agent's first software report…");
  const patchList = new CardList<InstalledPatchDto>({
    key: p => p.hotFixId,
    build: p => buildPatchRow(p)
  });
  const patchScroller = new BoundedScroller(280);
  patchScroller.content.controls.add(patchList.panel);
  const patchCard = card();
  patchCard.controls.add(
    stackOf(
      vstack({ spacing: GAP.sm, hAlign: 'stretch', autoWidth: false }),
      patchWaiting,
      patchScroller,
      text(
        'Not every update can be uninstalled — Windows refuses servicing-stack and some cumulative ' +
          'updates, and a reboot may be needed to finish.',
        { role: 'muted', font: FONT.tiny, wrap: true }
      )
    )
  );

  panel.controls.addRange([
    status.label,
    rebootBanner,
    updateSection,
    h2('Install software'),
    installCard,
    h2('Security'),
    avCard,
    softwareHeader,
    softwareCard,
    stackOf(inlineRow(), value('Windows updates', { font: FONT.h2 }), patchCount),
    patchCard
  ]);

  // ---------------------------------------------------------------
  //  Rows
  // ---------------------------------------------------------------

  /** A title over a detail line, with an action button on the right when there is one. */
  function actionRow(title: string, detail: string, action: Control | null): Control {
    const left = stackOf(
      vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false }),
      value(title, { font: FONT.bold, wrap: true })
    );
    if (detail) left.controls.add(muted(detail));

    const row = splitRow({ spacing: GAP.md, align: 'center' });
    fillWith(row, left);
    if (action) row.controls.add(action);
    return row;
  }

  function buildUpdateRow(u: PendingUpdateDto): Control {
    // A row stored before `UpdateId` existed cannot be installed on its own - Install All still
    // covers it - so it gets no button rather than one that would 404.
    const single =
      u.updateId.length > 0
        ? button('Install', () => void installUpdates(u), false, { enabled: dc.isOnline() })
        : null;
    if (single) singleUpdateButtons.push(single);
    return actionRow(u.title, updateDetail(u), single);
  }

  function buildSoftwareRow(s: InstalledSoftwareDto): Control {
    const uninstall = s.canUninstall
      ? button('Uninstall', () => void uninstallSoftware(s), false, { enabled: dc.isOnline() })
      : null;
    if (uninstall) softwareButtons.push(uninstall);
    return actionRow(s.name, softwareDetail(s), uninstall);
  }

  function buildPatchRow(p: InstalledPatchDto): Control {
    const uninstall = button('Uninstall', () => void uninstallPatch(p), false, {
      enabled: dc.isOnline()
    });
    patchButtons.push(uninstall);
    return actionRow(
      p.hotFixId,
      join([p.description, p.installedUtc ? `installed ${localDate(p.installedUtc)}` : null]),
      uninstall
    );
  }

  function buildJobRow(job: SoftwareInstallJobDto): Control {
    const left = stackOf(
      vstack({ spacing: 0, hAlign: 'stretch', autoWidth: false }),
      value(job.packageDisplayName, { font: FONT.bold, wrap: true })
    );
    if (job.message) left.controls.add(muted(job.message));
    left.controls.add(text(jobDetail(job), { role: 'muted', font: FONT.tiny, wrap: true }));

    const right = hstack({ spacing: GAP.sm, autoWidth: true, align: 'center' });
    if (job.hasPercent) right.controls.add(muted(`${job.percent}%`));
    // Queued, downloading and installing all read as one in-progress amber: three colours for three
    // stages of the same thing would be noise, and the label already says which stage it is at.
    right.controls.add(
      chip(
        job.stateLabel,
        job.state === SoftwareInstallState.Succeeded
          ? 'ok'
          : job.state === SoftwareInstallState.Failed
            ? 'danger'
            : 'warn'
      )
    );

    const row = splitRow({ spacing: GAP.md, align: 'center' });
    fillWith(row, left);
    row.controls.add(right);
    return row;
  }

  // Every per-row button gates on the device being online, and presence moves while the page is
  // open - so they are collected as they are built rather than hunted for in the tree afterwards.
  let softwareButtons: ReturnType<typeof button>[] = [];
  let patchButtons: ReturnType<typeof button>[] = [];
  let singleUpdateButtons: ReturnType<typeof button>[] = [];

  // ---------------------------------------------------------------
  //  Actions
  // ---------------------------------------------------------------

  async function refreshInventory(): Promise<void> {
    status.busy("Asking the agent for a fresh inventory…");
    try {
      await crm.refreshSoftwareInventory(dc.deviceId);
      if (dc.ctx.cancelled()) return;
      status.ok('Refresh requested — the lists update when the agent reports back.');
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  async function uninstallSoftware(s: InstalledSoftwareDto): Promise<void> {
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Uninstall software',
      message:
        `Uninstall ${s.name} from this device?\n\nThe uninstall runs silently on the machine and ` +
        'cannot be canceled once started.'
    });
    if (!ok || dc.ctx.cancelled()) return;
    status.busy(`Uninstalling ${s.name}…`);
    try {
      await crm.uninstallSoftware(dc.deviceId, s.key);
      if (dc.ctx.cancelled()) return;
      status.set(
        `Uninstalling ${s.name} — started; the result and a fresh list arrive when the device reports back.`
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  async function uninstallPatch(p: InstalledPatchDto): Promise<void> {
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Uninstall update',
      message:
        `Uninstall Windows update ${p.hotFixId} from this device?\n\nNot every update can be removed ` +
        '— the device reports back whether Windows allowed it. A reboot may be required to finish.'
    });
    if (!ok || dc.ctx.cancelled()) return;
    status.busy(`Uninstalling ${p.hotFixId}…`);
    try {
      await crm.uninstallPatch(dc.deviceId, p.hotFixId);
      if (dc.ctx.cancelled()) return;
      status.set(
        `Uninstalling ${p.hotFixId} — started; the result arrives when the device reports back.`
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  /** One update, or every pending one when `single` is absent. */
  async function installUpdates(single?: PendingUpdateDto): Promise<void> {
    const what = single ? `"${single.title}"` : 'every pending Windows update';
    const ok = await confirm(dc.ctx.desktop, {
      title: single ? 'Install update' : 'Install updates',
      message: single
        ? `Install ${what} on this device now?\n\nThe install runs silently in the background; a ` +
          'reboot may be needed to finish.'
        : 'Download and install every pending Windows update on this device now?\n\nThey install ' +
          'silently in the background; a reboot may be needed to finish.'
    });
    if (!ok || dc.ctx.cancelled()) return;
    status.busy(single ? `Installing ${single.title}…` : 'Installing pending Windows updates…');
    try {
      await crm.installUpdates(dc.deviceId, single?.updateId);
      if (dc.ctx.cancelled()) return;
      status.set(
        `${single ? `Installing ${single.title}` : 'Installing pending updates'} — started; the result ` +
          'and a fresh list arrive when the device reports back.'
      );
    } catch (error) {
      if (!dc.ctx.cancelled()) status.error(describeError(error));
    }
    dc.relayout();
  }

  /**
   * Pushes the selected package at this device.
   *
   * An offline device is NOT refused - its job is queued and runs on the agent's next connect.
   * Refusing would mean a technician has to remember to come back, which is the job the CRM is here
   * to do; the confirmation says which of the two is about to happen.
   */
  async function installPackage(): Promise<void> {
    if (installing) return;
    const selected = packagePicker.selectedItem;
    if (!selected) {
      installStatus.error('Pick a package to install.');
      dc.relayout();
      return;
    }

    const online = dc.isOnline();
    const ok = await confirm(dc.ctx.desktop, {
      title: 'Install software',
      message:
        `Install ${selected.displayName} on ${dc.device()?.name ?? 'this device'}?\n\n` +
        (online
          ? 'The installer downloads to the machine and runs silently as SYSTEM. It cannot be ' +
            'canceled once it starts.'
          : 'The device is offline, so this is queued — it runs the next time the agent connects.')
    });
    if (!ok || dc.ctx.cancelled()) return;

    installing = true;
    installButton.enabled = false;
    installStatus.busy(`Starting ${selected.name}…`);
    try {
      const result = await crm.installSoftware(selected.id, [dc.deviceId]);
      if (dc.ctx.cancelled()) return;
      if (result.jobs.length === 0) {
        // The refusal list is the half a bare "started" would hide: archived, still awaiting
        // approval, a retired record.
        installStatus.error(
          result.skipped.length > 0 ? result.skipped.join('; ') : 'The install did not start.'
        );
        return;
      }
      installStatus.ok(
        result.jobs[0]!.state === SoftwareInstallState.Queued
          ? `${selected.name} is queued — it installs the next time this device connects.`
          : `${selected.name} was sent to the device; progress appears below.`
      );
      await loadJobs();
    } catch (error) {
      if (!dc.ctx.cancelled()) installStatus.error(describeError(error));
    } finally {
      installing = false;
      installButton.enabled = true;
      dc.relayout();
    }
  }

  // ---------------------------------------------------------------
  //  Loading and applying
  // ---------------------------------------------------------------

  function applySoftwareRows(): void {
    const all = inventory?.software ?? [];
    const needle = filter.trim().toLowerCase();
    const matched = needle
      ? all.filter(
          s =>
            s.name.toLowerCase().includes(needle) || s.publisher.toLowerCase().includes(needle)
        )
      : all;
    softwareButtons = [];
    softwareList.set(matched);
    overflow.text =
      matched.length > MAX_SOFTWARE_ROWS
        ? `Showing ${MAX_SOFTWARE_ROWS} of ${matched.length} — type in the filter to narrow the list.`
        : '';
    overflow.visible = overflow.text.length > 0;
    softwareScroller.fit();
  }

  function applyInventory(): void {
    const waiting = inventory === null;
    avWaiting.visible = waiting;
    patchWaiting.visible = waiting;

    const av = inventory?.antivirus ?? [];
    avList.controls.clear();
    for (const product of av) {
      const row = inlineRow();
      row.controls.add(value(product.name, { font: FONT.bold }));
      row.controls.add(chip(product.enabled ? 'Enabled' : 'Disabled', product.enabled ? 'ok' : 'danger'));
      row.controls.add(
        chip(product.upToDate ? 'Definitions current' : 'Definitions out of date', product.upToDate ? 'ok' : 'warn')
      );
      avList.controls.add(row);
    }
    avList.visible = av.length > 0;
    // An inventory that arrived and named no antivirus at all is worth flagging; no inventory yet is
    // not - that is the waiting line's job.
    avNone.visible = inventory !== null && av.length === 0;

    softwareCount.text = inventory ? plural(inventory.software.length, 'application') : '';
    collected.text = inventory ? `Collected ${localDateTime(inventory.collectedUtc)}` : '';
    patchCount.text = inventory ? plural(inventory.patches.length, 'update') : '';

    applySoftwareRows();

    patchButtons = [];
    patchList.set(inventory?.patches ?? []);
    patchScroller.fit();

    const pending = inventory?.pendingUpdates ?? [];
    singleUpdateButtons = [];
    updateList.set(pending);
    updateScroller.fit();
    updateCount.text = pending.length === 0 ? '' : `${plural(pending.length, 'update')} waiting to install`;
    updateSection.visible = pending.length > 0;
    installAll.enabled = dc.isOnline();

    rebootBanner.visible = inventory?.pendingReboot ?? false;
    restartFromBanner.enabled = dc.isOnline();
  }

  function applyJobs(): void {
    jobList.set(jobs);
    jobScroller.fit();
    jobEmpty.text =
      jobs.length > 0 ? '' : 'Nothing has been installed from the library on this device yet.';
    jobEmpty.visible = jobs.length === 0;
  }

  function applyPackages(): void {
    packagePicker.items = packages;
    // Preselected: a picker sitting blank reads as a control that failed to load, and nothing is
    // installed without the confirmation, which names what it is about to run.
    packagePicker.selectedIndex = packages.length > 0 ? 0 : -1;
    packageEmpty.text =
      packages.length > 0
        ? ''
        : 'No installers in the library yet. Add one under Software in the sidebar — an uploaded ' +
          'file or a vendor URL, plus the switches that make it run silently.';
    packageEmpty.visible = packages.length === 0;
    installRow.visible = packages.length > 0;
  }

  async function load(): Promise<void> {
    try {
      inventory = await crm.getSoftwareInventory(dc.deviceId, dc.ctx.dispose.signal());
    } catch {
      // No inventory yet, or a device that has never reported one. The waiting line says so.
      inventory = null;
    }
    if (dc.ctx.cancelled()) return;
    applyInventory();

    try {
      // Filtered to what could actually be pushed: offering a package whose file is missing and
      // letting it fail at the device is strictly worse than not offering it, because the failure
      // would arrive minutes later describing a problem that lives on the server.
      packages = (await crm.getSoftwarePackages(false, dc.ctx.dispose.signal())).filter(
        p => p.canInstall
      );
    } catch {
      packages = [];
    }
    if (dc.ctx.cancelled()) return;
    applyPackages();

    await loadJobs();
    dc.relayout();
  }

  async function loadJobs(): Promise<void> {
    try {
      jobs = await crm.getSoftwareInstalls(dc.deviceId, 20, dc.ctx.dispose.signal());
    } catch {
      jobs = [];
    }
    if (dc.ctx.cancelled()) return;
    applyJobs();
  }

  void load();

  return {
    panel,
    async refresh() {
      try {
        inventory = await crm.getSoftwareInventory(dc.deviceId, dc.ctx.dispose.signal());
      } catch {
        inventory = null;
      }
      if (dc.ctx.cancelled()) return;
      applyInventory();
      dc.relayout();
    },
    applyInstallJob(job) {
      // The whole job arrives rather than a delta, so the row is replaced; a job id this page has
      // never seen - a fleet-wide deploy that included this machine while its page was open - is
      // prepended rather than dropped.
      const at = jobs.findIndex(j => j.id === job.id);
      jobs = at >= 0 ? jobs.map((j, i) => (i === at ? job : j)) : [job, ...jobs];
      applyJobs();
      // The device's own report supersedes whatever the request said: it is the only account of what
      // actually happened on the machine.
      installStatus.set(
        `${job.packageDisplayName}: ${job.message}`,
        job.isFinished
          ? job.state === SoftwareInstallState.Succeeded
            ? 'ok'
            : 'error'
          : 'busy'
      );
      relayout(panel);
    },
    applyOutcome(line) {
      status.set(line);
      relayout(panel);
    },
    attention() {
      return (inventory?.pendingReboot ?? false) || (inventory?.pendingUpdates?.length ?? 0) > 0;
    },
    applyPresence() {
      const online = dc.isOnline();
      installAll.enabled = online;
      restartFromBanner.enabled = online;
      for (const b of softwareButtons) b.enabled = online;
      for (const b of patchButtons) b.enabled = online;
      for (const b of singleUpdateButtons) b.enabled = online;
    }
  };
}

/** "KB ids · size · downloaded", omitting whatever is unknown. */
function updateDetail(u: PendingUpdateDto): string {
  return join([
    u.kbIds || null,
    u.sizeBytes && u.sizeBytes > 0 ? bytes(u.sizeBytes) : null,
    u.downloaded ? 'downloaded' : 'not yet downloaded'
  ]);
}

/** "version · publisher · size · installed date", omitting whatever is unknown. */
function softwareDetail(s: InstalledSoftwareDto): string {
  return join([
    s.version || null,
    s.publisher || null,
    s.sizeBytes && s.sizeBytes > 0 ? bytes(s.sizeBytes) : null,
    s.installedUtc ? `installed ${localDate(s.installedUtc)}` : null
  ]);
}

/** "by X · 14:05 · took 2m 10s · exit 3010", omitting whatever is unknown. */
function jobDetail(job: SoftwareInstallJobDto): string {
  const parts: Array<string | null> = [
    job.requestedBy ? `by ${job.requestedBy}` : null,
    localDateTime(job.requestedUtc)
  ];
  if (job.startedUtc && job.finishedUtc) {
    const seconds = Math.max(
      0,
      Math.round((Date.parse(job.finishedUtc) - Date.parse(job.startedUtc)) / 1000)
    );
    parts.push(seconds >= 60 ? `took ${Math.floor(seconds / 60)}m ${seconds % 60}s` : `took ${seconds}s`);
  }
  if (job.exitCode !== null) parts.push(`exit ${job.exitCode}`);
  return join(parts);
}
